/** Parse a rem string like "20rem" into its numeric value (20); 0 on garbage. */
export function parseRem(value: string): number {
  // Only accept rem strings; reject px/other units and garbage so a corrupted
  // stored value can't slip through as a raw number and skew clamping/aria.
  if (!value.trim().endsWith("rem")) return 0;
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

/** Format a rem number to a CSS string, snapped to 0.25rem (4px) steps. */
export function formatRem(rem: number): string {
  const snapped = Math.round(rem * 4) / 4;
  return `${snapped}rem`;
}

/** Clamp a rem number between min and max (rem numbers). */
export function clampRem(rem: number, minRem: number, maxRem: number): number {
  return Math.min(Math.max(rem, minRem), maxRem);
}

/** Convert a pixel delta to rem using the given root font size (default 16). */
export function pxDeltaToRem(deltaPx: number, rootPx = 16): number {
  return deltaPx / (rootPx || 16);
}

/**
 * Given the width (rem string) at drag start, a pixel delta since drag start,
 * and the sidebar side, return the next clamped width as a rem string.
 * A left sidebar widens when dragged right (positive delta); a right sidebar
 * widens when dragged left (negative delta).
 */
export function nextWidthFromDrag(
  startWidth: string,
  deltaPx: number,
  side: "left" | "right",
  minWidth: string,
  maxWidth: string,
  rootPx = 16,
): string {
  const startRem = parseRem(startWidth);
  const deltaRem = pxDeltaToRem(deltaPx, rootPx);
  const signed = side === "left" ? startRem + deltaRem : startRem - deltaRem;
  return formatRem(clampRem(signed, parseRem(minWidth), parseRem(maxWidth)));
}

/** Clamp a rem-string width to [min, max] and return a rem string. */
export function clampWidth(
  value: string,
  minWidth: string,
  maxWidth: string,
): string {
  return formatRem(
    clampRem(parseRem(value), parseRem(minWidth), parseRem(maxWidth)),
  );
}
